import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from '@mantine/core';
import Navigation from '../Home/Navigation';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import Dashboard from '../Dashboard/Dashboard';
import styles from './SoundGenerator.module.css';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/Auth/AuthContext';
import { uploadProjectMedia } from '../../services/mediaService';
import { uploadCaptionVideo, uploadCaptionVideoWithProgress } from '../../services/videoCaptionApi';
import { pollJobForCompletion } from '../../services/jobStatusService';
import { getApiUrl } from '../../config/api';
import { 
  fetchProjects, 
  Project, 
  deleteProject, 
  duplicateProject, 
  updateProjectStage,
  ProjectStage
} from '../../services/projectService';
import { projectService } from '../../services/projectService';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUpload, 
  faVideo,
  faMicrophone,
  faMusic, 
  faVolumeHigh,
  faMagicWandSparkles,
  faCommentDots,
  faCalendarAlt, 
  faClock,
  faEllipsisVertical,
  faTrash,
  faDownload,
  faLink,
  faHourglassHalf,
  faTimes,
  faCopy,
  faFolderOpen
} from '@fortawesome/free-solid-svg-icons';
import AudioModal from './AudioModal';
import SoundEffectsModal from './SoundEffectsModal';
import { CreateSoundProjectModal, NewSoundProjectButton } from './soundProjectUtils';

const SoundGenerator: React.FC = () => {
  const { t } = useTranslation('soundGenerator');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'all' | 'recent' | 'audio'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsWithVideos, setProjectsWithVideos] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [isLoadingVideoProjects, setIsLoadingVideoProjects] = useState(false);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Modal states - keeping only what's needed for backward compatibility
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [audioModalType, setAudioModalType] = useState<'upload' | 'melody'>('upload');
  const [isSoundEffectsModalOpen, setIsSoundEffectsModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  
  // Sound Project Modal state
  const [isSoundModalOpen, setIsSoundModalOpen] = useState(false);
  
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to check if a project is "create from scratch" audio
  const isCreateFromScratchAudio = (project: Project) => {
    return project.type === 'audio' &&
           project.originalVideoUrl === null &&
           project.originalAudioUrl === null &&
           project.finalAudio === null &&
           project.captionedVideoUrl === null &&
           project.generatedVideoUrl === null &&
           project.soundTrackVideoUrl === null;
  };

  // Function to get audio duration from URL
  const getAudioDuration = async (audioUrl: string): Promise<string> => {
    try {
      return new Promise((resolve) => {
        const audio = new Audio();
        audio.addEventListener('loadedmetadata', () => {
          const duration = audio.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          resolve(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        });
        
        audio.addEventListener('error', () => {
          resolve('0:00'); // Fallback if audio can't be loaded
        });
        
        audio.src = audioUrl;
      });
    } catch (error) {
      console.error('Error getting audio duration:', error);
      return '0:00';
    }
  };

  // Function to get video duration from URL
  const getVideoDuration = async (videoUrl: string): Promise<string> => {
    try {
      return new Promise((resolve) => {
        const video = document.createElement('video');
        video.addEventListener('loadedmetadata', () => {
          const duration = video.duration;
          const minutes = Math.floor(duration / 60);
          const seconds = Math.floor(duration % 60);
          resolve(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        });
        
        video.addEventListener('error', () => {
          resolve('0:00'); // Fallback if video can't be loaded
        });
        
        video.src = videoUrl;
      });
    } catch (error) {
      console.error('Error getting video duration:', error);
      return '0:00';
    }
  };

  // Function to get project duration - checks audio first, then video
  const getProjectDuration = async (project: Project): Promise<string> => {
    // Try to get duration from audio URLs first (prioritize finalAudio)
    const audioUrl = project.finalAudio || project.originalAudioUrl;
    if (audioUrl) {
      const duration = await getAudioDuration(audioUrl);
      if (duration && duration !== '0:00') {
        return duration;
      }
    }
    
    // If no audio or audio failed, try video URLs
    const videoUrl = project.generatedVideoUrl || project.captionedVideoUrl || project.originalVideoUrl;
    if (videoUrl) {
      const duration = await getVideoDuration(videoUrl);
      if (duration && duration !== '0:00') {
        return duration;
      }
    }
    
    // Last resort: use project's stored duration if valid
    if (project.duration && project.duration !== '00:00' && project.duration !== '0:00') {
      return project.duration;
    }
    
    return '0:00';
  };

  // State to store calculated durations
  const [projectDurations, setProjectDurations] = useState<Record<string, string>>({});

  // Effect to calculate durations for projects
  useEffect(() => {
    const calculateDurations = async () => {
      const newDurations: Record<string, string> = {};
      
      for (const project of projects) {
        const duration = await getProjectDuration(project);
        newDurations[project.id] = duration;
      }
      
      setProjectDurations(newDurations);
    };

    if (projects.length > 0) {
      calculateDurations();
    }
  }, [projects]);

  // Fetch projects when component mounts
  useEffect(() => {
    let isMounted = true;
    
    const loadProjects = async () => {
      if (!isMounted) return;
      
      setIsProjectsLoading(true);
      setError(null);
      
      try {
        const response = await fetchProjects(1, 1000);
        
        if (!isMounted) return;
        
        if (response && 'projects' in response) {
          // Show projects with video content that can have soundtracks added
          // AND audio-only projects created from scratch
          const relevantProjects = response.projects.filter(p => {
            // Check for any of the three video URL fields (for soundtrack addition)
            const hasOriginalVideo = p.originalVideoUrl && p.originalVideoUrl.trim() !== '';
            const hasCaptionedVideo = p.captionedVideoUrl && p.captionedVideoUrl.trim() !== '';  
            const hasGeneratedVideo = p.generatedVideoUrl && p.generatedVideoUrl.trim() !== '';
            
            // Check for "create from scratch" audio projects
            const isFromScratchAudio = p.type === 'audio' &&
                                     p.originalVideoUrl === null &&
                                     p.originalAudioUrl === null &&
                                     p.finalAudio === null &&
                                     p.captionedVideoUrl === null &&
                                     p.generatedVideoUrl === null &&
                                     p.soundTrackVideoUrl === null;
            
            console.log('Project relevance check:', {
              id: p.id,
              title: p.title,
              type: p.type,
              stage: p.stage,
              hasOriginalVideo,
              hasCaptionedVideo,
              hasGeneratedVideo,
              isFromScratchAudio,
              originalVideoUrl: p.originalVideoUrl,
              captionedVideoUrl: p.captionedVideoUrl,
              generatedVideoUrl: p.generatedVideoUrl,
              willShow: hasOriginalVideo || hasCaptionedVideo || hasGeneratedVideo || isFromScratchAudio
            });
            
            // Show projects that have video content OR are audio-only "from scratch" projects
            return hasOriginalVideo || hasCaptionedVideo || hasGeneratedVideo || isFromScratchAudio;
          });
          
          setProjects(relevantProjects);
          console.log('Filtered projects (video + from scratch audio):', 
            relevantProjects.map(p => ({
              id: p.id,
              title: p.title,
              type: p.type,
              stage: p.stage
            }))
          );
        } else {
          setProjects([]);
          console.warn('API returned unexpected data format');
        }
        
      } catch (err) {
        if (!isMounted) return;
        
        console.error('Error fetching projects:', err);
        setError('Failed to load projects. Please try again later.');
        setProjects([]);
        
        // Only show notification if still mounted and not a network error during unmount
        if (isMounted) {
          showNotification({
            title: 'Error',
            message: 'Failed to load projects',
            type: 'error'
          });
        }
      } finally {
        if (isMounted) {
          setIsProjectsLoading(false);
        }
      }
    };

    loadProjects();
    
    return () => {
      isMounted = false;
    };
  }, []); // ✅ Remove showNotification dependency to prevent re-runs

  // Sound Project Modal handlers
  const handleOpenSoundModal = () => {
    setIsSoundModalOpen(true);
  };

  const handleCloseSoundModal = () => {
    setIsSoundModalOpen(false);
  };

  const handleSoundProjectSuccess = (projectId: string) => {
    // Navigate to sound editor with project ID
    navigate(`/sound-editor/${projectId}`);
  };

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Basic validation
      if (!file.type.startsWith('audio/')) {
        showNotification({
          title: 'Invalid File',
          message: 'Please select an audio file',
          type: 'error'
        });
        return;
      }
      
      setIsLoading(true);
      
      try {
        // Upload the file and create a project
        const uploadResult = await uploadProjectMedia([file]);
        
        if (uploadResult.success && uploadResult.projectId) {
          // showNotification({
          //   title: 'Success',
          //   message: 'Audio file uploaded successfully! Redirecting to editor...',
          //   type: 'success'
          // });
          
          // Navigate to audio editor with the uploaded file
          setTimeout(() => {
            if (uploadResult.uploadedFiles && uploadResult.uploadedFiles.length > 0) {
              const audioUrl = encodeURIComponent(uploadResult.uploadedFiles[0].url);
              navigate(`/sound-editor/${uploadResult.projectId}?audioUrl=${audioUrl}`);
            } else {
              navigate(`/sound-editor/${uploadResult.projectId}`);
            }
          }, 1000);
        } else {
          throw new Error('Upload failed - no project ID returned');
        }
        
      } catch (error) {
        console.error("Upload failed:", error);
        showNotification({
          title: 'Upload Failed',
          message: 'There was an error uploading your audio file',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle modal open for different types
  const handleOpenModal = (type: 'upload' | 'melody') => {
    setAudioModalType(type);
    setIsAudioModalOpen(true);
  };

  // Handle sound effects modal
  const handleOpenSoundEffectsModal = () => {
    setIsSoundEffectsModalOpen(true);
  };

  const handleAudioGenerated = (audioUrl: string, projectId: string, type: 'melody') => {
    console.log('Audio generated:', { audioUrl, projectId, type });
    
    // Show success notification
    // showNotification({
    //   title: 'Success',
    //   message: `${type === 'melody' ? 'Soundtrack' : 'Audio'} generated successfully! Redirecting to sound editor...`,
    //   type: 'success'
    // });
    
    // Close modal
    setIsAudioModalOpen(false);
    
    // Navigate to audio editor with the new audio
    setTimeout(() => {
      const encodedAudioUrl = encodeURIComponent(audioUrl);
      navigate(`/sound-editor/${projectId}`);
    }, 1000); // Small delay to let user see the success message
  };

  const handleSoundEffectGenerated = (audioUrl: string, filename: string, duration: number) => {
    console.log('Sound effect generated:', { audioUrl, filename, duration });
    
    // Show success notification
    // showNotification({
    //   title: 'Success',
    //   message: 'Sound effect generated successfully! Redirecting to audio editor...',
    //   type: 'success'
    // });
    
    // Close modal
    setIsSoundEffectsModalOpen(false);
    
    // Navigate to audio editor with the new sound effect
    setTimeout(() => {
      const encodedAudioUrl = encodeURIComponent(audioUrl);
      // For sound effects, we'll navigate to sound editor with a new project
      // The sound editor can handle creating a new project if needed
      navigate(`/sound-editor/new?audioUrl=${encodedAudioUrl}&filename=${encodeURIComponent(filename)}&duration=${duration}&type=sound-effect`);
    }, 1000);
  };

  // Handle video upload for soundtrack generation
  const handleVideoUpload = async (file: File) => {
    try {
      setIsUploadingVideo(true);
      setJobId(''); // Clear previous jobId
      
      // showNotification({
      //   title: 'Uploading Video',
      //   message: 'Please wait while we upload your video...',
      //   type: 'info'
      // });
      
      // Create FormData to include the no_caption flag
      const formData = new FormData();
      formData.append('file', file);
      formData.append('no_caption', 'true'); // Add flag for sound app
      formData.append('title', `Soundtrack Project - ${new Date().toLocaleDateString()}`); // Custom title
      
      // Use fetch directly to send FormData with flag
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(getApiUrl('/video/upload-video-for-caption-with-progress'), {
        method: 'POST',
        headers,
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Upload failed - server error');
      }
      
      const result = await response.json();
      console.log('Upload response:', result);
      
      if (result && result.jobId) {
        console.log(`🆔 Setting job ID: ${result.jobId}`);
        setJobId(result.jobId);
        
        // showNotification({
        //   title: 'Upload Complete',
        //   message: 'Video uploaded! Processing for soundtrack editing...',
        //   type: 'success'
        // });
      } else {
        console.error('No jobId in response:', result);
        throw new Error('Upload failed - no job ID returned');
      }
      
    } catch (error) {
      console.error('Error uploading video:', error);
      showNotification({
        title: 'Error',
        message: `Failed to upload video: ${error.message || 'Please try again.'}`,
        type: 'error'
      });
    } finally {
      setIsUploadingVideo(false);
    }
  };

  // Handle Dashboard completion callback
  const processData = (videoUrl: string, projectId: string) => {
    console.log('Dashboard processing complete:', { videoUrl, projectId });
    
    // showNotification({
    //   title: 'Success',
    //   message: 'Video processed successfully! Redirecting to sound editor...',
    //   type: 'success'
    // });
    
    setIsProcessing(false); // Clear processing state when complete
    
    // Navigate to sound editor with the project ID
    setTimeout(() => {
      navigate(`/sound-editor/${projectId}`);
    }, 1000);
  };

  // Handle file input change for video upload
  const handleVideoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        showNotification({
          title: 'Invalid File',
          message: 'Please select a valid video file',
          type: 'error'
        });
        return;
      }
      
      // Validate file size (max 2GB)
      const maxSize = 2 * 1024 * 1024 * 1024; // 2GB in bytes
      if (file.size > maxSize) {
        showNotification({
          title: 'File Too Large',
          message: 'File size too large. Please select a video under 2GB.',
          type: 'error'
        });
        return;
      }
      
      handleVideoUpload(file);
    }
  };

  // Handle load existing video projects
  const handleLoadVideoProjects = async () => {
    try {
      setIsLoadingVideoProjects(true);
      
      // Use the same fetch method as the main projects loading
      const response = await fetchProjects(1, 1000);
      console.log('Video projects response:', response);
      
      let allProjects = [];
      
      if (response && 'projects' in response) {
        allProjects = response.projects;
      } else if (Array.isArray(response)) {
        allProjects = response;
      }
      
      console.log('All projects from response:', allProjects);
      console.log('Sample project structure:', allProjects[0]);
      
      // Filter for video projects using the same approach as main display
      const videoProjects = allProjects.filter(project => {
        const hasVideoUrl = (project.originalVideoUrl && project.originalVideoUrl.trim() !== '') ||
                           (project.generatedVideoUrl && project.generatedVideoUrl.trim() !== '');
        const isVideoType = project.type === 'video';
        const hasVideoInTitle = project.title && (
          project.title.toLowerCase().includes('video') ||
          project.title.toLowerCase().includes('frame')
        );
        const isVideoStage = project.stage === ProjectStage.CAPTION || 
                           project.stage === ProjectStage.CONTENT;
        
        console.log('Video project check:', {
          id: project.id,
          title: project.title,
          type: project.type,
          stage: project.stage,
          hasVideoUrl,
          isVideoType,
          hasVideoInTitle,
          isVideoStage
        });
        
        return hasVideoUrl || isVideoType || hasVideoInTitle || isVideoStage;
      });
      
      console.log('Filtered video projects:', videoProjects);
      setProjectsWithVideos(videoProjects);
      
      if (videoProjects.length === 0) {
        showNotification({
          title: 'No Video Projects',
          message: 'You don\'t have any video projects yet. Upload a video to get started!',
          type: 'info'
        });
        return;
      }
      
      // Show the project selection modal
      setIsProjectModalOpen(true);
      
    } catch (error) {
      console.error('Error loading video projects:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to load video projects. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoadingVideoProjects(false);
    }
  };

  // Handle project selection from dropdown
  const handleProjectSelection = (projectId: string) => {
    setSelectedProjectId(projectId);
    setIsProjectModalOpen(false);
    
    // showNotification({
    //   title: 'Project Selected',
    //   message: 'Opening Sound Editor...',
    //   type: 'success'
    // });
    
    // Navigate to sound editor with the selected project
    setTimeout(() => {
      navigate(`/sound-editor/${projectId}`);
    }, 500);
  };

  // Toggle dropdown visibility
  const toggleActionDropdown = (projectId: string) => {
    setActiveActionDropdown(activeActionDropdown === projectId ? null : projectId);
  };

  // Handle project delete
  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      showNotification({
        title: 'Success',
        message: 'Project deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification({
        title: 'Delete Failed',
        message: 'There was an error deleting the project',
        type: 'error'
      });
    }
  };

  // Handle project duplication
  const handleDuplicateProject = async (projectId: string) => {
    try {
      const newProject = await duplicateProject(projectId);
      setProjects([newProject, ...projects]);
      // showNotification({
      //   title: 'Success',
      //   message: 'Project duplicated successfully',
      //   type: 'success'
      // });
    } catch (error) {
      console.error("Duplication failed:", error);
      showNotification({
        title: 'Duplication Failed',
        message: 'There was an error duplicating the project',
        type: 'error'
      });
    }
  };

  // Handle continue working on project
  const handleContinueProject = async (project: Project) => {
    console.log('Continue project:', { 
      projectId: project.id, 
      projectType: project.type, 
      originalAudioUrl: project.originalAudioUrl
    });
    
    try {
      // Update project stage to EDIT when navigating to edit page
      await updateProjectStage(project.id, ProjectStage.EDIT);
      
      // Navigate to audio editor with project ID and audio URL
      if (project.originalAudioUrl) {
        const audioUrl = encodeURIComponent(project.originalAudioUrl);
        navigate(`/sound-editor/${project.id}?audioUrl=${audioUrl}`);
      } else {
        // Navigate to sound editor even without existing audio
        navigate(`/sound-editor/${project.id}`);
        // showNotification({
        //   title: 'Sound Editor Opened',
        //   message: 'You can upload audio files to start editing.',
        //   type: 'info'
        // });
      }
      
    } catch (error) {
      console.error("Error updating project stage:", error);
      showNotification({
        title: 'Error',
        message: 'Failed to access project',
        type: 'error'
      });
    }
  };

  // Filter projects based on selectedOption and searchTerm  
  const filteredProjects = projects.filter(project => {
    // Filter by type
    if (selectedOption === 'audio') {
      // Show both regular audio projects and "create from scratch" audio projects
      const isRegularAudio = project.type === 'audio';
      const isFromScratchAudio = isCreateFromScratchAudio(project);
      if (!isRegularAudio && !isFromScratchAudio) return false;
    }
    if (selectedOption === 'recent') {
      // Get projects from the last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const projectDate = new Date(project.date);
      if (projectDate < sevenDaysAgo) return false;
    }
    
    // Filter by search term
    if (searchTerm && !project.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  // Calculate project counts for filter buttons
  const projectCounts = {
    all: projects.length,
    recent: projects.filter(project => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const projectDate = new Date(project.date);
      return projectDate >= sevenDaysAgo;
    }).length,
    audio: projects.filter(project => 
      project.type === 'audio' || isCreateFromScratchAudio(project)
    ).length
  };

  // Function to format the date in a more readable way
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className={styles.outerContainer}>
      <SEO
        title={seoConfig.soundGenerator.title}
        description={seoConfig.soundGenerator.description}
        keywords={seoConfig.soundGenerator.keywords}
        url="https://kingdomstudio.pro/sound-generation"
      />
      <Navigation />
      <main className={styles.container}>
        <div className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.header}>
              <h1 className={styles.title}>
                <FontAwesomeIcon icon={faMusic} className={styles.titleIcon} />
                {t('hero.title')}
              </h1>
              <p className={styles.subtitle}>
                {t('hero.subtitle')}
              </p>
              
              <div className={styles.headerActions}>
                <Button
                  className={styles.createButton}
                  size="lg"
                  onClick={() => document.getElementById('video-upload-input')?.click()}
                  loading={isUploadingVideo || isProcessing}
                  disabled={isUploadingVideo || isProcessing}
                  leftSection={<FontAwesomeIcon icon={faVideo} />}
                >
                  {isUploadingVideo ? t('hero.uploading') : isProcessing ? t('hero.processingVideo') : t('hero.uploadVideo')}
                </Button>
                
                <NewSoundProjectButton onClick={handleOpenSoundModal} />
              </div>
            </div>

            {/* Hidden file input */}
            <input
              type="file"
              accept="audio/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
        </div>
        
        {/* Dashboard for Video Processing */}
        {jobId && (
          <div className={styles.dashboardContainer}>
            <Dashboard 
              jobId={jobId} 
              callBackFunc={processData} 
              onProcessingStateChange={setIsProcessing}
            />
          </div>
        )}
        
        {/* Projects Section */}
        <div className={styles.projectsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('projectsList.title')}</h2>
            <div className={styles.searchAndFilter}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder={t('projectsList.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.filterOptions}>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'all' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('all')}
                >
                  {t('projectsList.filters.all')} ({projectCounts.all})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'recent' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('recent')}
                >
                  {t('projectsList.filters.recent')} ({projectCounts.recent})
                </button>
                <button 
                  className={`${styles.filterButton} ${selectedOption === 'audio' ? styles.active : ''}`}
                  onClick={() => setSelectedOption('audio')}
                >
                  {t('projectsList.filters.audio')} ({projectCounts.audio})
                </button>
              </div>
            </div>
          </div>
          
          {isProjectsLoading ? (
            <div className={styles.loadingContainer}>
              <Loader size="xl" />
              <p>{t('projectsList.loading')}</p>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <p className={styles.errorMessage}>{error}</p>
              <Button onClick={() => window.location.reload()} className={styles.retryButton}>
                {t('projectsList.retry')}
              </Button>
            </div>
          ) : projects.length > 0 ? (
            <div className={styles.projectGrid}>
              {filteredProjects.map(project => (
                <div 
                  key={project.id} 
                  className={`${styles.projectCard} 
                    ${activeActionDropdown === project.id ? styles.cardWithActiveDropdown : ''}
                  `}
                >
                  <div className={styles.projectThumbnail}>
                    {/* Use video preview for projects with video URLs */}
                    {(project.originalVideoUrl || project.generatedVideoUrl) ? (
                      <video 
                        src={project.generatedVideoUrl || project.originalVideoUrl}
                        className={styles.videoPreview}
                        // muted
                        preload="metadata"
                        onError={(e) => {
                          // Fallback to thumbnail image if video fails to load
                          const videoElement = e.target as HTMLVideoElement;
                          const img = document.createElement('img');
                          img.src = project.thumbnail || getApiUrl('/public/assets/audio-waveform.svg');
                          img.alt = project.title;
                          img.className = styles.videoPreview;
                          img.onerror = () => {
                            img.src = getApiUrl('/public/assets/audio-waveform.svg');
                          };
                          videoElement.parentNode?.replaceChild(img, videoElement);
                        }}
                      />
                    ) : (
                      <img 
                        src={project.thumbnail && project.thumbnail.trim() !== '' ? project.thumbnail : getApiUrl('/public/assets/audio-waveform.svg')} 
                        alt={project.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          console.log('Image load error for project:', project.id, 'Original src:', target.src);
                          target.src = getApiUrl('/public/assets/audio-waveform.svg');
                        }} 
                        onLoad={(e) => {
                          const target = e.target as HTMLImageElement;
                          console.log('Image loaded successfully for project:', project.id, 'src:', target.src);
                        }}
                      />
                    )}
                    <div className={styles.projectType}>
                      <FontAwesomeIcon icon={faMicrophone} />
                    </div>
                    <div className={styles.projectDuration}>
                      <FontAwesomeIcon icon={faClock} />
                      <span>{projectDurations[project.id] || project.duration || '0:00'}</span>
                    </div>
                  </div>
                  
                  <div className={styles.projectInfo}>
                    <div className={styles.titleRow}>
                      <h3 className={styles.projectTitle}>{project.title}</h3>
                      {isCreateFromScratchAudio(project) && (
                        <span className={styles.fromScratchBadge} title="Created from scratch">
                          <FontAwesomeIcon icon={faMagicWandSparkles} />
                        </span>
                      )}
                    </div>
                    <div className={styles.projectMeta}>
                      <span className={styles.projectDate}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {formatDate(project.date)}
                      </span>
                    </div>
                  </div>
                  
                  <div className={styles.projectActions}>
                    <Button 
                      className={styles.continueButton}
                      onClick={() => handleContinueProject(project)}
                    >
                      {t('projectCard.continue')}
                    </Button>
                    <div className={styles.moreActions}>
                      <button 
                        className={styles.moreButton}
                        onClick={() => toggleActionDropdown(project.id)}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      {activeActionDropdown === project.id && (
                        <div className={`${styles.actionDropdown} ${activeActionDropdown === project.id ? styles.active : ''}`}
                             onClick={(e) => e.stopPropagation()}
                        >
                          {project.originalAudioUrl && (
                            <a 
                              className={styles.actionButton}
                              href={project.originalAudioUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              <FontAwesomeIcon icon={faDownload} /> {t('projectCard.downloadAudio')}
                            </a>
                          )}
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDuplicateProject(project.id)}
                          >
                            {t('projectCard.duplicate')}
                          </button>
                          <button className={styles.actionButton}>{t('projectCard.share')}</button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} /> {t('projectCard.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noProjects}>
              <FontAwesomeIcon icon={faPlus} size="2x" className={styles.noProjectsIcon} />
              <h3>{t('projectsList.noProjects')}</h3>
              <p>{t('projectsList.noProjectsMessage')}</p>
            </div>
          )}
        </div>
      </main>
      
      {/* Audio Generation Modal */}
      <AudioModal
        isOpen={isAudioModalOpen}
        onClose={() => setIsAudioModalOpen(false)}
        onAudioGenerated={handleAudioGenerated}
        type={audioModalType}
        hideAllPromptsTab={true} // Hide "All Prompts" tab on landing page
      />
      
      {/* Sound Effects Modal */}
      <SoundEffectsModal
        isOpen={isSoundEffectsModalOpen}
        onClose={() => setIsSoundEffectsModalOpen(false)}
        onSoundEffectGenerated={handleSoundEffectGenerated}
        userId={user?.id || ''}
      />

      {/* Project Selection Modal */}
      {isProjectModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: '#2a2a2a',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h3 style={{
                color: 'white',
                margin: 0,
                fontSize: '18px',
                fontWeight: '600'
              }}>
                Select Video Project
              </h3>
              <button
                onClick={() => setIsProjectModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#a0a0a0',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => (e.target as HTMLButtonElement).style.color = 'white'}
                onMouseLeave={(e) => (e.target as HTMLButtonElement).style.color = '#a0a0a0'}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{
              maxHeight: '60vh',
              overflowY: 'auto',
              padding: '20px'
            }}>
              {projectsWithVideos.length > 0 ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '12px'
                }}>
                  {projectsWithVideos.map((project) => (
                    <div
                      key={project.id}
                      onClick={() => handleProjectSelection(project.id)}
                      style={{
                        padding: '16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                    >
                      <div style={{
                        width: '50px',
                        height: '35px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        backgroundColor: '#1a1a1a',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        {project.originalVideoUrl || project.generatedVideoUrl ? (
                          <video
                            src={project.originalVideoUrl || project.generatedVideoUrl}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            // muted
                            playsInline
                            preload="metadata"
                          />
                        ) : (
                          <FontAwesomeIcon icon={faVideo} style={{ color: '#8b5cf6', fontSize: '16px' }} />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          color: 'white', 
                          fontSize: '16px', 
                          fontWeight: '500',
                          marginBottom: '4px'
                        }}>
                          {project.title}
                        </div>
                        <div style={{ 
                          color: '#a0a0a0', 
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <FontAwesomeIcon icon={faClock} style={{ fontSize: '12px' }} />
                          {project.duration || 'Unknown duration'}
                        </div>
                      </div>
                      <FontAwesomeIcon icon={faVideo} style={{ color: '#8b5cf6', fontSize: '18px' }} />
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: '#a0a0a0'
                }}>
                  <FontAwesomeIcon icon={faVideo} style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <p>No video projects found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Sound Project Modal */}
      <CreateSoundProjectModal
        isOpen={isSoundModalOpen}
        onClose={handleCloseSoundModal}
        onSuccess={handleSoundProjectSuccess}
      />
      
      {/* Hidden File Input for Video Upload */}
      <input
        id="video-upload-input"
        type="file"
        accept="video/*"
        style={{ display: 'none' }}
        onChange={handleVideoFileChange}
      />
    </div>
  );
};

export default SoundGenerator;